import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Plus, ChevronLeft, ChevronRight, Layout, 
  Calendar as CalendarIcon, List, Eye, ExternalLink, X,
  Video, Image, Mail, Mic, Pencil, Loader2, CheckCircle2, AlertTriangle
} from 'lucide-react';
import FullCalendar from "@fullcalendar/react";
import daygridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import RightSidebar from './RightSidebar'; 
import taskService from '../../api/taskService'; 

import { useOutletContext } from 'react-router-dom'; // Add this to your imports at the top

const NO_CAPTION_TEXT = "No Caption";

const DailyStackView = () => {
  // Grab everything from the Dashboard context
  const { 
    isDark, 
    onTaskClick, 
    activeFilters = {}, 
    onDataLoaded,
    brands = [],
    postTypes = [],
    users = [] 
  } = useOutletContext();
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dailyStack_viewMode') || 'weekly';
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem('dailyStack_selectedDate');
    return savedDate ? new Date(savedDate) : new Date();
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const calendarRef = useRef(null);
  
  const fetchingRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : { name: "Yadnesh", role: "staff", id: 1 };
  });
  
  const secondaryText = isDark ? 'text-slate-300' : 'text-slate-600';

  // Persist viewMode and selectedDate to localStorage
  useEffect(() => {
    localStorage.setItem('dailyStack_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('dailyStack_selectedDate', selectedDate.toISOString());
  }, [selectedDate]);

  useEffect(() => {
    if (onDataLoaded && posts.length > 0) {
      onDataLoaded(posts);
    }
  }, [posts, onDataLoaded]);

  const getDerivedStatus = (post) => {
    if (post.posted_at) return "Posted";
    if (post.confirmed_at) return "Confirmed";
    if (post.readied_at) return "Ready";
    return "Pending";
  };

  const filteredPosts = useMemo(() => {
  if (!posts || posts.length === 0) return [];
  
  return posts.filter(post => {
    const p = post.extendedProps || {};
    const filters = activeFilters || {};

    // 1. BRAND (ID: brand_name)
    if (filters.brand_name?.length > 0) {
      if (!filters.brand_name.includes(p.brand_name)) return false;
    }

    // 2. POST TYPE (ID: type_name)
    // p.post_type comes from task.type_name in your new mapping
    if (filters.type_name?.length > 0) {
      if (!filters.type_name.includes(p.post_type)) return false;
    }

    // 3. STAFF (ID: ready_by_name)
    if (filters.ready_by_name?.length > 0) {
      if (!filters.ready_by_name.includes(p.ready_by_name)) return false;
    }

    // 4. STATUS (ID: status)
    // Note: This logic now correctly handles "Not Ready", "Not Posted" etc.
    if (filters.status?.length > 0) {
      const currentStatus = p.status || getDerivedStatus(p);
      
      const matchStatus = filters.status.some(filterValue => {
        if (filterValue === "Posted") return !!p.posted_at;
        if (filterValue === "Not Posted") return !p.posted_at;
        if (filterValue === "Confirmed") return !!p.confirmed_at;
        if (filterValue === "Not Confirmed") return !p.confirmed_at;
        if (filterValue === "Ready") return !!p.readied_at;
        if (filterValue === "Not Ready") return !p.readied_at;
        if (filterValue === "Claimed") return !!p.ready_by_id;
        if (filterValue === "Not Claimed") return !p.ready_by_id;
        if (filterValue === "Pending") return !p.readied_at && !p.confirmed_at && !p.posted_at;
        return filterValue === currentStatus;
      });
      
      if (!matchStatus) return false;
    }

    // 5. GLOBAL SEARCH
    if (filters.global_search?.length > 0) {
      const query = filters.global_search[0].toLowerCase();
      // Added safety joins so it doesn't crash on null brand names
      const searchStr = `${p.brand_name || ''} ${p.post_name || ''} ${p.post_type || ''}`.toLowerCase();
      if (!searchStr.includes(query)) return false;
    }

    return true;
  });
}, [posts, activeFilters]);

  const fetchTasks = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    
    try {
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      const data = await taskService.getTasksByMonth(month, year);
      
      const mappedPosts = data.map(task => {
  const dateOnly = task.task_due_date.split('T')[0];
  
  // Important: Ensure the status is derived BEFORE nesting so it's consistent
  const derivedStatus = getDerivedStatus(task);

  return {
    id: task.id.toString(),
    title: task.brand_name || "Untitled", 
    start: dateOnly,
    allDay: true,
    extendedProps: {
      ...task,
      // 1. BRAND: Ensure this matches the 'brand_name' key in config
      brand_name: task.brand_name || "Unknown Brand",

      // 2. POST TYPE: Your config uses id: 'type_name' 
      // but the filter logic in the view checks p.post_type.
      // We map task.type_name to post_type to bridge that gap.
      post_type: task.type_name || "General",

      // 3. STAFF: Matches 'ready_by_name' in config
      ready_by_name: task.ready_by_name || (task.ready_by_id ? "Claimed" : "Unassigned"),

      // 4. STATUS: Matches 'status' in config
      status: derivedStatus,

      // 5. SEARCH: Include post_title for the global search bar
      post_name: task.post_title?.trim() || NO_CAPTION_TEXT,
      
      actual_time: task.task_due_date,
    }
  };
});
      setPosts(mappedPosts);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [selectedDate.getFullYear(), selectedDate.getMonth()]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.updateSize();
        // Sync calendar to the restored selectedDate
        calendarApi.gotoDate(selectedDate);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [viewMode]);

const handlePostClick = (postData) => {
  if (onTaskClick) {
    onTaskClick(postData);
  }
};
  const handleSaveChanges = async (updatedPost) => {
    try {
        await taskService.updateTask(updatedPost.id, updatedPost);
        
        // Find names from our lists based on the IDs returned/saved
        const brandObj = brands.find(b => b.id == updatedPost.brand_id);
        const typeObj = postTypes.find(t => t.id == updatedPost.post_type_id);
        const userObj = users.find(u => u.id == updatedPost.posted_by_id);

        setPosts(prevPosts => {
          return prevPosts.map(p => {
            if (p.id === updatedPost.id.toString()) {
              return {
                ...p,
                title: brandObj?.brand_name || p.title,
                extendedProps: {
                  ...p.extendedProps,
                  ...updatedPost,
                  brand_name: brandObj?.brand_name || p.extendedProps.brand_name,
                  type_name: typeObj?.type_name || p.extendedProps.type_name,
                  post_type: typeObj?.type_name || p.extendedProps.post_type,
                  posted_by_name: userObj?.full_name || p.extendedProps.posted_by_name,
                  status: getDerivedStatus(updatedPost)
                }
              };
            }
            return p;
          });
        });
    } catch (err) {
        console.error("Save error:", err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      // Remove the deleted task from the local state
      setPosts(prevPosts => prevPosts.filter(p => p.id !== taskId.toString()));
     // Close the sidebar
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete task. Please try again.");
    }
  };

  const formatTitle = (text = "", maxLength = 45) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getStatusStyles = (post) => {
    const status = post.status || getDerivedStatus(post);
    if (status === "Posted") {
      return { 
        bg: isDark ? "bg-emerald-950/20" : "bg-emerald-50/30", 
        border: isDark ? "border-emerald-800/50" : "", 
        accent: "bg-emerald-500",
        calendar: "bg-emerald-500 text-white" 
      };
    }
    if (status === "Pending") {
      return { 
        bg: isDark ? "bg-orange-950/20" : "bg-orange-50/30", 
        border: isDark ? "border-orange-800/50" : "", 
        accent: "bg-orange-500",
        calendar: "bg-orange-500 text-white"
      };
    }
    if (post.confirmed_at || status === "Confirmed" || status === "Ready") {
      return { 
        bg: isDark ? "bg-[#2D4D94]/20" : "bg-[#2D4D94]/200", 
        border: isDark ? "border-[#2D4D94]/50" : "border-[#2D4D94]/5", 
        accent: "bg-[#2D4D94]",
        calendar: "bg-[#2D4D94] text-white"
      };
    }
    return { 
      bg: isDark ? "bg-slate-800/40" : "bg-slate-50/50", 
      border: isDark ? "border-slate-700" : "", 
      accent: "bg-slate-400",
      calendar: "bg-slate-400 text-white"
    };
  };

  const getPostTypeIcon = (type = "") => {
    const t = type.toLowerCase();
    const iconSize = 18; 
    if (t.includes('reel') || t.includes('video')) return <Video size={iconSize} />;
    if (t.includes('canva') || t.includes('image') || t.includes('post')) return <Image size={iconSize} />;
    if (t.includes('mail')) return <Mail size={iconSize} />;
    if (t.includes('engagement')) return <Mic size={iconSize} />;
    return <Layout size={iconSize} />;
  };

  const changeDate = (offset) => {
    const next = new Date(selectedDate);
    if (viewMode === 'monthly') {
        next.setMonth(selectedDate.getMonth() + offset);
    } else {
        const jump = viewMode === 'weekly' ? 7 * offset : offset;
        next.setDate(selectedDate.getDate() + jump);
    }
    setSelectedDate(next);
    if (calendarRef.current) calendarRef.current.getApi().gotoDate(next);
  };

  const getWeekDates = (startDate) => {
    const monday = new Date(startDate);
    const day = monday.getDay();
    const diff = monday.getDate() - day + (day === 0 ? -6 : 1); 
    monday.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  };

  const weekDates = getWeekDates(selectedDate);

  const StatusTag = ({ label, color, isActive }) => {
    const variants = {
      green: isDark ? "bg-emerald-900/30 border-emerald-700 text-emerald-400" : "bg-emerald-50 border-emerald-500 text-emerald-600",
      orange: isDark ? "bg-orange-900/30 border-orange-700 text-orange-400" : "bg-orange-50 border-orange-500 text-orange-600",
      slate: isDark ? "bg-slate-800 border-slate-700 text-slate-500" : "bg-slate-50 border-slate-300 text-slate-400"
    };
    const style = isActive ? variants[color] : variants.orange;
    return (
      <span className={`px-2 py-1 rounded-md border text-[8px] font-black uppercase tracking-widest whitespace-nowrap ${style}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 relative p-2 transition-colors duration-300">
      <div className={`flex-1 rounded-[32px] border flex flex-col pt-3 pl-0 transition-all duration-300 ${
        isDark ? 'bg-slate-800/60 border-slate-800' : 'bg-slate-100 border-slate-100'
      }`}>
        
        {/* Header Section */}
        <div className={`pr-8 pb-4 shrink-0 border-b transition-colors ${isDark ? 'border-slate-800' : 'border-slate-50'}`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className={`px-5 py-2.5 rounded-xl border min-w-[140px] text-center transition-colors ${
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'
              }`}>
                <h1 className={`text-sm md:text-xl font-bold uppercase tracking-tighter transition-colors ${
                  isDark ? 'text-slate-200' : 'text-slate-600'
                }`}>
                  {viewMode === 'weekly' 
                    ? `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('en-US', { day: 'numeric' })}`
                    : viewMode === 'monthly'
                    ? selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    : selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                </h1>
              </div>
              <div className={`flex items-center rounded-xl p-1 border transition-colors ${
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'
              }`}>
                <button onClick={() => changeDate(-1)} className={`p-2.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ChevronLeft size={18}/></button>
                <button 
                  onClick={() => {
                    const today = new Date();
                    setSelectedDate(today);
                    if (calendarRef.current) calendarRef.current.getApi().gotoDate(today);
                  }} 
                  className={`mx-1 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 ${
                    isDark 
                      ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Today
                </button>
                <button onClick={() => changeDate(1)} className={`p-2.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ChevronRight size={18}/></button>
              </div>
            </div>

            <div className={`flex p-1.5 rounded-xl border transition-colors ${
              isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'
            }`}>
              {[
                { id: 'today', label: 'Day', icon: <List size={14}/> }, 
                { id: 'weekly', label: 'Week', icon: <Layout size={14}/> }, 
                { id: 'monthly', label: 'Month', icon: <CalendarIcon size={14}/> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setViewMode(tab.id)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                    viewMode === tab.id 
                      ? isDark ? 'bg-slate-700 text-[#5A9AFF] shadow-sm border border-slate-600' : 'bg-white text-[#2D4D94] shadow-sm border border-slate-100' 
                      : isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.icon} <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* View Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading && posts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
               <Loader2 className="animate-spin text-indigo-500" size={40} />
            </div>
          ) : viewMode !== 'monthly' ? (
            <div className="flex-1 overflow-y-auto pr-8 pt-0 custom-scrollbar ">
              <div className="space-y-8 w-full max-w-[1600px] mx-auto">
                {(viewMode === 'today' ? [selectedDate] : weekDates).map((date) => {
                  const dayPosts = filteredPosts.filter(p => new Date(p.extendedProps.actual_time).toDateString() === date.toDateString());
                  const isToday = new Date().toDateString() === date.toDateString();
                  return (
                    <div key={date.toISOString()} className="space-y-4 pt-4">
                      <div className="flex items-center gap-2 px-2">
                      
                        <h3 className={`text-sm font-bold  uppercase tracking-[0.2em] ${isToday ? 'text-orange-600' : secondaryText}`}>
                          {date.toLocaleDateString('en-US', { weekday: 'long' })}
                        </h3>
                        <span className={`text-[11px] font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        <div className={`flex-1 h-[1px] ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`}></div>
                      </div>
                      <div className="space-y-3">
                        {dayPosts.length > 0 ? (
                          dayPosts.map((post) => {
                            const p = post.extendedProps;
                            const styles = getStatusStyles(p);
                            return (
                              <div 
                                key={post.id} 
                                onClick={() => handlePostClick(p)} 
                                className={`group relative cursor-pointer py-5 px-8 rounded-2xl border-2 transition-all flex items-center shadow-sm hover:shadow-md active:scale-[0.99] overflow-hidden ${styles.bg} ${styles.border}`}
                              >
                                <div className={`absolute left-0 top-0 bottom-0 w-2 ${styles.accent} group-hover:w-3 transition-all duration-300`} />

                                <div className="flex-1 grid grid-cols-1 md:grid-cols-[1.8fr_0.8fr_auto] gap-4 items-center min-w-0 relative z-10">
                                  {/* Title & Assignment - Spans more width, title allows 2 lines */}
                                  <div className="flex flex-col gap-1 min-w-0">
                                    <div className={`text-sm md:text-base font-bold  tracking-tight transition-colors line-clamp-2 leading-tight ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                                      {p.brand_name}: <span className={`${secondaryText} font-semibold`}>{p.post_name?.trim() || NO_CAPTION_TEXT}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className={`flex items-center gap-1.5 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                                        <Pencil size={11} className="opacity-60" />
                                        <span className={`text-[12px] font-black tracking-wide ${secondaryText}`}>
                                          {p.ready_by_name || p.created_by_name || "Unassigned"}
                                        </span>
                                      </div>
                                      <StatusTag label={p.readied_at ? 'Ready' : 'Not Ready'} color={p.readied_at ? 'green' : 'orange'} isActive={!!p.readied_at} />
                                    </div>
                                  </div>

                                  {/* Post Type - Centered in its own stable column */}
                                  <div className={`flex items-center gap-3 px-6 border-l transition-colors ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                    <div className={`${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{getPostTypeIcon(p.post_type)}</div>
                                    <span className={`text-[11px] font-black uppercase tracking-widest ${secondaryText}`}>{p.post_type}</span>
                                  </div>

                                  {/* Confirmed & Posted tags - Grouped together in one row */}
                                  <div className={`flex flex-row items-center gap-2 shrink-0 px-6 border-l transition-colors ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                    <StatusTag label={p.confirmed_at ? "Confirmed" : "Not Confirmed"} color="green" isActive={!!p.confirmed_at} />
                                    <StatusTag label={p.posted_at ? "Posted" : "Not Posted"} color="green" isActive={!!p.posted_at} />
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : <div className="h-1 opacity-0"></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={`flex-1 overflow-y-auto p-4 transition-colors custom-scrollbar ${
              isDark ? 'bg-slate-900/40' : 'bg-slate-100'
            }`}>
<div className={`w-full max-w-[1600px] mx-auto rounded-3xl shadow-sm border ${
  isDark ? 'bg-slate-900 border-slate-800' : 'bg-slate-100 border-slate-100'
}`}>                <FullCalendar
                  key={`calendar-${viewMode}-${filteredPosts.length}`} 
                  ref={calendarRef}
                  plugins={[daygridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  initialDate={selectedDate}
                  events={filteredPosts}
                  height="720px" 
                  dayMaxEvents={3}
                  headerToolbar={false}
                  eventDisplay="block"
                  eventContent={(info) => <CalendarEvent info={info} getStatusStyles={getStatusStyles} />}
                  eventClick={(info) => handlePostClick(info.event.extendedProps)}
                />
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

const CalendarEvent = ({ info, getStatusStyles }) => {
  const p = info.event.extendedProps;
  const styles = getStatusStyles(p);
  const hasCaptionDescription = Boolean(p.description?.trim());
  const captionStatusText = hasCaptionDescription ? "Caption Ready" : "Caption Not Ready";
  const CaptionStatusIcon = hasCaptionDescription ? CheckCircle2 : AlertTriangle;
  
  return (
    <div className="w-full group px-0.5">
      <div className={`
        relative p-1.5 rounded-lg  transition-all duration-200 
         active:scale-[0.98]
        ${styles.calendar}
      `}>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-[10px] font-black uppercase tracking-tight truncate">
            {p.brand_name}
          </span>
          <div className="flex items-center gap-1 min-w-0 opacity-90">
            <CaptionStatusIcon size={10} className="shrink-0" />
            <span className="text-[8px] font-bold truncate">
              {captionStatusText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyStackView;
